import React, { useCallback, useEffect, useMemo } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { relationshipContactSearch } from '@/utils/typesense/clients/relationshipContactSearch';
import { joinFilters } from '@/utils/constants';
import { TableRefresh } from '@/components/table/TableRefresh';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import RelationshipContactsTableFilters from './RelationshipContactsTableFilters';
import {
	changeRowsPerPage,
	onOrderChange,
	setOrder,
	setOrderBy,
} from '@/features/table/relationshipContactsTableSlice';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import {
	RELATIONSHIP_CONTACT_DEFAULT_HIDDEN_COLUMNS,
	RELATIONSHIP_CONTACT_HEAD_CELLS,
} from '@/utils/relationshipContactTableSync';
import { toRelationshipContactStatuses } from '@/utils/relationshipContactOptions';

const RelationshipContactsTable = ({ status }) => {
	const dispatch = useDispatch();
	const { pathname: slug } = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const { rowsPerPage, orders } = useSelector((state) => state.relationshipContactsTable);
	const orderParams = useMemo(
		() => orders[slug] ?? { order: 'desc', orderBy: 'lastUpdated' },
		[orders, slug],
	);
	const { order, orderBy } = orderParams;
	const { owners, ownerFilter, filters } = useInstantSearchOwnerFilters();
	const createdByFilter = ownerFilter ? `createdBy: [${owners.join(',')}]` : '';
	useEffect(() => {
		if (orders[slug]?.orderBy === 'lastUpdated' && orders[slug]?.order === 'desc') {
			return;
		}
		dispatch(setOrderBy({ slug, orderBy: orderBy ?? 'lastUpdated' }));
		dispatch(setOrder({ slug, order: order ?? 'desc' }));
	}, [dispatch, order, orderBy, orders, slug]);

	useEffect(() => {
		dispatch(onOrderChange());
	}, [dispatch, orderBy, order]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const openContactDrawer = useCallback(
		(row) => {
			if (!row?.id) return;
			dispatch(
				openDialog({
					dialogName: 'relationshipContact',
					data: { contactId: row.id },
				}),
			);
		},
		[dispatch],
	);

	const typesenseFilters = useMemo(() => {
		const selectedStatuses = toRelationshipContactStatuses(filters.relationshipStatus);
		const statusFilter =
			selectedStatuses.length === 0
				? ''
				: `relationshipStatus:=[${selectedStatuses.join(',')}]`;

		return joinFilters([
			brokerGroupIds.length > 0 ? `brokerGroupId:[${brokerGroupIds.join(',')}]` : '',
			statusFilter,
			createdByFilter,
		]);
	}, [brokerGroupIds, createdByFilter, filters.relationshipStatus]);

	const columns = useMemo(() => parseHeadCells(RELATIONSHIP_CONTACT_HEAD_CELLS), []);

	return (
		<InstantSearch indexName='relationshipContacts' searchClient={relationshipContactSearch}>
			<Configure filters={typesenseFilters} />
			<TableRefresh table='relationshipContactsTable' />
			<RelationshipContactsTableFilters />
			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType='relationshipContact'
				columns={columns}
				tab={status}
				variant={slug}
				enableExport={false}
				showPreview={false}
				enableRowClick
				onRowClick={({ row }) => openContactDrawer(row)}
				defaultHiddenColumns={RELATIONSHIP_CONTACT_DEFAULT_HIDDEN_COLUMNS}
			/>
		</InstantSearch>
	);
};

RelationshipContactsTable.displayName = 'RelationshipContactsTable';

export default RelationshipContactsTable;
